import { Tabs } from "@newsela/angelou";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import {
  CITATION_COPY_BUTTON_TEXT,
  CITATION_OPTION_VALUES,
} from "../../CitationAction.constant";
import { ICitationFormInput } from "../../CitationAction.interface";
import { mountCitation } from "../../utils";
import {
  tabsContainerCss,
  tabsCss,
  tabsListCss,
  tabsPanelCss,
  textCss,
} from "./CitationGeneratorBody.style";
import CitationGeneratorBodyPanel from "./CitationGeneratorBodyPanel";
import CitationGeneratorBodyButtons from "./CitationGeneratorBodyButtons";

interface CitationGeneratorBodyProps {
  citationData: ICitationFormInput;
  setShowEditForm: (showEditForm: boolean) => void;
}

const CitationGeneratorBody = ({
  setShowEditForm,
  citationData,
}: CitationGeneratorBodyProps) => {
  const [copyButtonText, setCopyButtonText] = useState(
    CITATION_COPY_BUTTON_TEXT.default,
  );
  const [citation, setCitation] = useState<ReactNode>("citation");
  const [showBanner, setShowBanner] = useState(false);
  const [hasMissingFields, setHasMissingFields] = useState(false);

  const handleTabClick = (event?: MouseEvent) => {
    const target = event?.target as HTMLElement;
    const tab =
      target?.id || target?.offsetParent?.id || CITATION_OPTION_VALUES.MLA.tab;

    setCopyButtonText(CITATION_COPY_BUTTON_TEXT.default);
    const response = mountCitation({ citationData, tab, hasMissingFields });
    setCitation(response);

    const { datePublished } = citationData;
    const isRequiredFieldMissing = !datePublished;

    setHasMissingFields(isRequiredFieldMissing);
    setShowBanner(isRequiredFieldMissing);
  };

  /*
    Formatting the citation in MLA style, as it is the default
    selection when the citation modal opens.
  */
  useEffect(() => {
    handleTabClick();
  }, [hasMissingFields]);

  const citationBody = (
    <CitationGeneratorBodyPanel
      citation={citation}
      showBanner={showBanner}
      setShowBanner={setShowBanner}
    />
  );

  const initialPanelId = CITATION_OPTION_VALUES.MLA.panelId;
  const panels = Object.values(CITATION_OPTION_VALUES).map(
    ({ name, panelId }) => ({
      name,
      panelId,
      panelContent: citationBody,
    }),
  );

  return (
    <div css={tabsContainerCss} id="citation_modal_body">
      <p css={textCss}>
        Choose a citation style from the options below. Some information may be
        missing, please edit it manually.
      </p>
      <Tabs
        horizontalSwitch="sm"
        initialPanelId={initialPanelId}
        flavor="underlined"
        __cssFor={{
          root: tabsCss,
          tabPanels: tabsPanelCss,
          tabList: tabsListCss,
        }}
        onClick={(event) => handleTabClick(event)}
        panels={panels}
        renderTabsAsButtons
      />
      <CitationGeneratorBodyButtons
        citation={citation}
        setShowEditForm={setShowEditForm}
        copyButtonText={copyButtonText}
        setCopyButtonText={setCopyButtonText}
      />
    </div>
  );
};

export default CitationGeneratorBody;
